/**
 * Client-side shape of the asset Equipment tab — assembled by
 * `useEquipmentTab` from the existing `/assets/{uid}/properties` endpoint
 * (no bespoke backend route). The hook groups the property values by
 * source (`manual` vs `derivation`) and projects them into the
 * `DerivedPropertyCard` shape that Zone A renders.
 *
 * Manual override is just a regular property value with
 * `source = "manual"` and `priority = 10` — high enough to win the
 * `priority DESC NULLS LAST` ordering used by ListAssetPropertyValues.
 * Clearing an override stamps `end_date = now` on the active row. There
 * is no `/derived-properties` or `/manual` endpoint.
 */

import type { AssetInspection } from '../hooks/useComplianceApi'

export type EquipmentCondition =
  | 'new'
  | 'good'
  | 'fair'
  | 'poor'
  | 'end_of_life'
  | 'unassessed'

/**
 * Whether this equipment row is batch-tracked (a single record represents
 * multiple physical assets, with a `batch_count`). Replaces the legacy
 * `tracking_mode: 'unit' | 'batch'` enum (per spec 03).
 */
export type EquipmentMultiple = boolean

export type DerivedPropertyState = 'computed' | 'manual'

export type DerivedPropertyValue =
  | string
  | number
  | boolean
  | null
  | string[]
  | number[]
  | Record<string, unknown>

export interface EquipmentTabAsset {
  id: string
  type: string // asset_type code (building, building_group, floor, unit, parcel_land)
  name: string
}

/** Equipment row referenced by a derivation card's source attribution. */
export interface EquipmentTabSourceEquipmentRef {
  id: string
  label?: string
  location_label?: string
}

/** Audit attribution for a manually-edited card. */
export interface EquipmentTabEditedBy {
  id: string
  name: string
}

/**
 * Frontend view of a Zone A card. Composed client-side by
 * `useAssetDerivedProperties` from the merged property values. Fields
 * mirror the original schema: required-but-nullable for the audit/source
 * attribution so consumers can `??`-coalesce uniformly.
 */
export interface DerivedPropertyCard {
  /** Property code (e.g. `heating_fuel`). */
  key: string
  /** Locale-resolved display label. */
  label: string
  /** Property type (e.g. `enum`, `number`, `boolean`). */
  type: string
  /** "computed" when the displayed value comes from the derivation row,
   *  "manual" when a manual override is active. */
  state: DerivedPropertyState
  /** Latest derivation row's value, when present. */
  computed_value: DerivedPropertyValue | null
  /** Active manual-override value, when present. */
  manual_value: DerivedPropertyValue | null
  /** What the UI should render — manual_value if set, otherwise computed_value. */
  displayed_value: DerivedPropertyValue | null
  /** Equipment that fed the derivation, if any. */
  source_equipment: EquipmentTabSourceEquipmentRef[]
  last_computed_at: string | null
  edited_by: EquipmentTabEditedBy | null
  edited_at: string | null
  edited_reason: string | null
  /** Optional flag — when truthy, the card has a small shield icon on it. */
  feeds_compliance?: boolean
}

/**
 * A contract actually linked to an equipment through `contract_assets`.
 * Surfaced on the row so the Maintenance / Garantie columns can render a link
 * to the contract page instead of an unclickable boolean icon.
 */
export interface EquipmentContractRef {
  uid: string
  name: string
}

export interface EquipmentRow {
  id: string
  name: string
  /** Asset-type label (e.g. "Chaudière gaz à condensation"). */
  type_label?: string | null
  location_label?: string | null
  /** ISO date (YYYY-MM-DD) when the equipment was installed. */
  installed_at?: string | null
  lifespan_years?: number | null
  age_years?: number | null
  condition: EquipmentCondition
  /**
   * True when this row is batch-tracked. Replaces the legacy
   * `tracking_mode: 'unit' | 'batch'` field — `multiple === true` ↔ batch,
   * `multiple === false` ↔ unit.
   */
  multiple?: EquipmentMultiple | null
  batch_count?: number | null
  /** Asset.quantity — passed straight through from the source row so the UI
   *  can show "× N" when the user explicitly created a multi-quantity asset. */
  quantity?: number | null
  /** Optional brand/model — useful for the row sub-line if `type_label` is absent. */
  brand?: string | null
  model?: string | null
  /**
   * The asset's `start_date` column (ISO `YYYY-MM-DD`) — the single source of
   * truth for an equipment's commissioning date since spec 2026-09-03-02,
   * which retired the `commissioning_year` property. This is what the editable
   * "mise en service" column reads and writes; `installed_at` above mirrors it
   * for the age/lifespan bar.
   */
  start_date?: string | null
  /**
   * True when the asset actually carries an `equipment_condition` property
   * value. `condition === 'unassessed'` is ambiguous (no value *or* an
   * unparseable one), and the inline editor needs to know whether to PATCH an
   * existing value or POST a new one.
   */
  has_condition_value?: boolean
  /**
   * Maintenance contract linked to this equipment through `contract_assets`.
   * Null when no contract is linked — the icon then reads "no contract", which
   * is honest, where the previous `maintenance_contract` boolean property was
   * not backed by any contract entity at all.
   */
  maintenance_contract?: EquipmentContractRef | null
  /**
   * Works contract (`CONTRACTING_WORK`) linked to this equipment — the entity
   * a warranty hangs off. Null when none is linked.
   */
  warranty_contract?: EquipmentContractRef | null
  /**
   * Periodic inspections (rules of class `periodic_inspection`) evaluated on
   * this equipment — the Vérifications column. Empty when none applies.
   */
  inspections?: AssetInspection[]
  /**
   * Equipment components (spec 2026-10-27-17): the assembly this row is part
   * of, when that assembly is listed here — the row then renders nested under
   * it, never in its own trade's bucket.
   */
  assembly_uid?: string | null
  /** The components nested under this row (`part_of`), at any depth. */
  components?: EquipmentRow[]
  /** True when a filter matched a component: the assembly opens expanded. */
  components_expanded?: boolean
  /**
   * Name of the assembly whose maintenance contract covers this component,
   * when the component has none of its own (« via Chaudière 1 »).
   */
  maintenance_via?: string | null
}

export interface ConditionRollup {
  poor: number
  end_of_life: number
  unassessed: number
}

export interface EquipmentTradeBucket {
  /** asset_types.code value (e.g. `trade_hvac`). */
  trade_code: string
  /** asset_types.uid value of the trade. */
  trade_uid?: string
  /**
   * Locale-resolved display label. The backend hydrates this from
   * `asset_types.name` based on the org's i18n settings.
   */
  trade_label: string
  /** Optional emoji from the asset type. */
  trade_emoji?: string | null
  /** MDI icon name from the asset type — what AssetTypeTile draws. */
  trade_icon?: string | null
  count: number
  condition_rollup: ConditionRollup
  equipment: EquipmentRow[]
  /**
   * Every asset_type UID whose inheritance chain reaches this trade —
   * including the trade itself. Used by the Equipment-tab "+" button to
   * constrain the asset-type dropdown.
   */
  descendant_asset_type_uids?: string[]
}

export interface EquipmentTabTotals {
  equipment_count: number
  trades_with_at_risk: number
}

export interface EquipmentTabResponse {
  asset: EquipmentTabAsset
  derived_properties: DerivedPropertyCard[]
  equipment_by_trade: EquipmentTradeBucket[]
  totals: EquipmentTabTotals
}

/**
 * Body sent by the Zone A "set manual value" dialog. The hook in
 * `useEquipmentTab` translates this into a regular property POST with
 * `source = "manual"` + `priority = 10`.
 */
export interface SetManualDerivedPropertyRequest {
  /** The new value (any JSON shape accepted by the property's type). */
  value: DerivedPropertyValue | null
  /** Optional human-readable explanation, persisted in source_details. */
  reason?: string
}

export const EQUIPMENT_CONDITIONS: EquipmentCondition[] = [
  'new',
  'good',
  'fair',
  'poor',
  'end_of_life',
  'unassessed',
]

// SPEC_TRADE_CODES has moved to ../lib/equipment/tradeOrder.ts so it survives
// the deletion of equipment-tab types (spec 2026-05-05-03). Re-exported here
// for backward compatibility with existing imports.
export { SPEC_TRADE_CODES, type SpecTradeCode } from '../lib/equipment/tradeOrder'
